import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Container, Row, Col, Card, CardBody, CardTitle, CardText, Form, FormGroup, Label, Input, Button, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchAllPhotos } from "../utils/apiService";
import { photoDelete } from "../utils/apiService";
import { photoUpload } from "../utils/apiService";
import { getImage } from "../utils/apiService";

const Upload = ({ uploadTypeOverride, hideStyleExamples }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [selectedChoice, setSelectedChoice] = useState(2); // Default to Selfie Only
  const [newUploadedFiles, setNewUploadedFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [imageUrls, setImageUrls] = useState({});
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDropzoneEnabled, setIsDropzoneEnabled] = useState(false);
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setNewUploadedFiles([file]);
      setIsUploading(true);
      setUploadError(null);
      
      try {
        // Get access token for authentication
        let token = null;
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          console.log('No access token available for upload:', e);
        }
        
        // Call photoUpload with the selected file and token
        const result = await photoUpload(file, token);
        console.log('Photo upload successful:', result);
        
        // Reload photos after successful upload
        const loadPhotos = async () => {
          try {
            let token = null;
            try {
              token = await getAccessTokenSilently();
            } catch (e) {
              console.log('No access token available:', e);
            }
            
            const photos = await fetchAllPhotos(token);
            console.log('Reloaded photos after upload:', photos);
            
            // Extract fileList from the API response structure
            if (photos && photos.uploadConfigs && photos.uploadConfigs.length > 0) {
              const fileList = photos.uploadConfigs[0].fileList || [];
              setExistingFiles(fileList);
              
              // Load images for each file
              if (token) {
                fileList.forEach(file => {
                  loadImage(file.fileId, token);
                });
              }
            } else {
              setExistingFiles([]);
            }
          } catch (error) {
            console.error('Error reloading photos after upload:', error);
          }
        };
        
        await loadPhotos();
      } catch (error) {
        console.error('Photo upload failed:', error);
        setUploadError(error.message);
      } finally {
        setIsUploading(false);
      }
    }
  }, [getAccessTokenSilently]);

  // Function to load individual image
  const loadImage = async (fileId, accessToken) => {
    try {
      setImageLoadingStates(prev => ({ ...prev, [fileId]: true }));
      const imageBlob = await getImage(fileId, accessToken);
      const imageUrl = URL.createObjectURL(imageBlob);
      setImageUrls(prev => ({ ...prev, [fileId]: imageUrl }));
    } catch (error) {
      console.error(`Error loading image ${fileId}:`, error);
    } finally {
      setImageLoadingStates(prev => ({ ...prev, [fileId]: false }));
    }
  };

  // Load photos on component mount
  useEffect(() => {
    const loadPhotos = async () => {
      try {
        let token = null;
        try {
          token = await getAccessTokenSilently();
        } catch (e) {
          console.log('No access token available:', e);
        }
        
        const photos = await fetchAllPhotos(token);
        console.log('Loaded photos:', photos);
        
        // Extract fileList from the API response structure
        if (photos && photos.uploadConfigs && photos.uploadConfigs.length > 0) {
          const fileList = photos.uploadConfigs[0].fileList || [];
          setExistingFiles(fileList);
          
          // Load images for each file
          if (token) {
            fileList.forEach(file => {
              loadImage(file.fileId, token);
            });
          }
        } else {
          setExistingFiles([]);
        }
      } catch (error) {
        console.error('Error loading photos:', error);
        setExistingFiles([]);
      }
    };

    loadPhotos();
  }, [getAccessTokenSilently]);

  // Cleanup effect to revoke object URLs when component unmounts
  useEffect(() => {
    return () => {
      Object.values(imageUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [imageUrls]);

  // Enable dropzone when there are no files or when files are deleted
  useEffect(() => {
    if (existingFiles.length === 0) {
      setIsDropzoneEnabled(true);
    }
  }, [existingFiles.length]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg']
    },
    multiple: false,
    disabled: !isDropzoneEnabled || existingFiles.length > 0
  });

  const choices = [
    {
      id: 1,
      title: "Selfie + Body",
      description: "Most accurate fit",
      icon: "🧍‍♀️"
    },
    {
      id: 2,
      title: "Selfie Only", 
      description: "Face swap and shop",
      icon: "👩"
    },
    {
      id: 3,
      title: "Body Only",
      description: "Manual measurements",
      icon: "📏"
    }
  ];

  const handleChoiceSelect = (choiceId) => {
    if (choiceId === 1 || choiceId === 3) {
      setShowComingSoonModal(true);
    } else {
      setSelectedChoice(choiceId);
    }
  };

  const handlePhotoDelete = async (fileId) => {
    try {
      const accessToken = await getAccessTokenSilently();
      await photoDelete(fileId, accessToken);
      console.log(`Photo ${fileId} deleted successfully`);
      
      // Remove the deleted file from the existing files list
      setExistingFiles(prevFiles => prevFiles.filter(file => file.fileId !== fileId));
      
      // Clean up the image URL and loading state
      setImageUrls(prev => {
        const newUrls = { ...prev };
        if (newUrls[fileId]) {
          URL.revokeObjectURL(newUrls[fileId]);
          delete newUrls[fileId];
        }
        return newUrls;
      });
      setImageLoadingStates(prev => {
        const newStates = { ...prev };
        delete newStates[fileId];
        return newStates;
      });
    } catch (error) {
      console.error('Error deleting photo:', error);
      // TODO: Show error message to user
    }
  };

  const renderQuestions = () => {
    if (!selectedChoice) return null;

    switch (selectedChoice) {
      case 1: // Selfie + Body
        return (
          <Card className="mt-4">
            <CardBody>
              <CardTitle tag="h4" className="mb-3">Selfie + Body Configuration</CardTitle>
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="height">Height (cm)</Label>
                      <Input type="number" id="height" placeholder="Enter your height" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="weight">Weight (kg)</Label>
                      <Input type="number" id="weight" placeholder="Enter your weight" />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="bodyType">Body Type</Label>
                  <Input type="select" id="bodyType">
                    <option>Select body type</option>
                    <option>Slim</option>
                    <option>Athletic</option>
                    <option>Average</option>
                    <option>Curvy</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="selfieUpload">Upload Selfie</Label>
                  <Input type="file" id="selfieUpload" accept="image/*" />
                </FormGroup>
                <Button color="primary" className="mt-3">Continue</Button>
              </Form>
            </CardBody>
          </Card>
        );

      case 2: // Selfie Only
        return (
          <Card className="mt-4">
            <CardBody>
              <CardTitle tag="h4" className="mb-3">Selfie Configuration</CardTitle>
              <Form>
                <FormGroup>
                  <Label>Upload Selfie</Label>
                  <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'drag-active' : ''}`}
                    style={{
                      border: '2px dashed #dee2e6',
                      borderRadius: '10px',
                      padding: '40px 20px',
                      textAlign: 'center',
                      backgroundColor: (!isDropzoneEnabled || existingFiles.length > 0) ? '#e9ecef' : '#f8f9fa',
                      cursor: (!isDropzoneEnabled || existingFiles.length > 0) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: (!isDropzoneEnabled || existingFiles.length > 0) ? 0.6 : 1
                    }}
                  >
                    <input {...getInputProps()} />
                    {existingFiles.length > 0 ? (
                      <div>
                        <div className="mb-3">
                          <span style={{ fontSize: '2rem' }}>🔒</span>
                        </div>
                        <p className="mb-2 text-muted">Upload disabled - selfie already added</p>
                        <p className="text-muted small mb-0">
                          Delete existing selfie to upload a new one
                        </p>
                      </div>
                    ) : !isDropzoneEnabled ? (
                      <div>
                        <div className="mb-3">
                          <span style={{ fontSize: '2rem' }}>🔒</span>
                        </div>
                        <p className="mb-2 text-muted">Upload disabled</p>
                        <p className="text-muted small mb-0">
                          Enable upload to continue
                        </p>
                      </div>
                    ) : isDragActive ? (
                      <p className="mb-0">Drop the image here...</p>
                    ) : (
                      <div>
                        <div className="mb-3">
                          <span style={{ fontSize: '2rem' }}>📷</span>
                        </div>
                        <p className="mb-2">Drag & drop your selfie here, or click to select</p>
                        <p className="text-muted small mb-0">
                          Supports: PNG, JPG, JPEG
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {isUploading && (
                    <div className="mt-3">
                      <p className="text-info mb-2">
                        <strong>⏳ Uploading...</strong> Please wait
                      </p>
                    </div>
                  )}
                  
                  {uploadError && (
                    <div className="mt-3">
                      <p className="text-danger mb-2">
                        <strong>✗ Upload failed:</strong> {uploadError}
                      </p>
                    </div>
                  )}
                  
                  {newUploadedFiles.length > 0 && !isUploading && !uploadError && (
                    <div className="mt-3">
                      <p className="text-success mb-2">
                        <strong>✓ Image uploaded:</strong> {newUploadedFiles[0].name}
                      </p>
                    </div>
                  )}
                </FormGroup>
              </Form>
              
              {/* Description text */}
              <div className="mt-4 text-center">
                <p className="text-muted mb-3">
                  Upload a selfie to see how different styles look on you with our face swap technology.
                </p>
              </div>
              
              {/* Explanatory text */}
              <div className="mt-4">
                <p className="text-muted text-center mb-3">
                  Your selfies are used to enhance how products are displayed when browsing participating Funely providers
                </p>
              </div>
              
              {/* Previously Uploaded Selfies */}
              <div className="mt-4">
                <h6 className="mb-3">{existingFiles.length} of 3 Uploaded Selfies</h6>
                <Row>
                  {[0, 1, 2].map((index) => {
                    const file = existingFiles[index];
                    return (
                      <Col key={index} md={4} className="mb-2">
                        <div style={{ position: 'relative' }}>
                          <div 
                            style={{
                              width: '100%',
                              height: '120px',
                              backgroundColor: '#f8f9fa',
                              border: '2px solid #dee2e6',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                              cursor: index > 0 ? 'pointer' : 'default',
                              transition: 'all 0.3s ease'
                            }}
                            onClick={index > 0 ? () => setShowComingSoonModal(true) : undefined}
                          >
                            {file ? (
                              imageLoadingStates[file.fileId] ? (
                                <div style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column'
                                }}>
                                  <div style={{ 
                                    width: '20px', 
                                    height: '20px', 
                                    border: '2px solid #f3f3f3',
                                    borderTop: '2px solid #3498db',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                  }}></div>
                                  <span style={{ 
                                    fontSize: '0.7rem', 
                                    color: '#6c757d',
                                    marginTop: '5px'
                                  }}>
                                    Loading...
                                  </span>
                                </div>
                              ) : imageUrls[file.fileId] ? (
                                <img 
                                  src={imageUrls[file.fileId]} 
                                  alt={file.fileName} 
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover' 
                                  }} 
                                />
                              ) : (
                                <div style={{ 
                                  width: '100%', 
                                  height: '100%', 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexDirection: 'column',
                                  padding: '10px'
                                }}>
                                  <span style={{ fontSize: '2rem', marginBottom: '5px' }}>📷</span>
                                  <span style={{ 
                                    fontSize: '0.8rem', 
                                    color: '#6c757d',
                                    textAlign: 'center',
                                    wordBreak: 'break-word'
                                  }}>
                                    {file.fileName}
                                  </span>
                                  <span style={{ 
                                    fontSize: '0.7rem', 
                                    color: '#adb5bd' 
                                  }}>
                                    {file.fileExtension}
                                  </span>
                                </div>
                              )
                            ) : (
                              <div style={{ 
                                width: '100%', 
                                height: '100%', 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                padding: '10px'
                              }}>
                                <span style={{ color: '#adb5bd', fontWeight: '400', marginBottom: '10px' }}>
                                  Selfie {index + 1}
                                </span>
                                {index === 0 && existingFiles.length === 0 && (
                                  <div style={{
                                    fontSize: '0.5rem',
                                    color: 'white',
                                    backgroundColor: '#1A9E96',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    fontWeight: 'bold'
                                  }}>
                                    WAITING FOR UPLOAD
                                  </div>
                                )}
                                {index > 0 && (
                                  <div 
                                    style={{
                                      fontSize: '0.5rem',
                                      color: 'white',
                                      backgroundColor: '#6c757d',
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      fontWeight: 'bold'
                                    }}
                                  >
                                    COMING SOON
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {file && (
                            <>
                              <div style={{
                                position: 'absolute',
                                top: '8px',
                                left: '8px',
                                fontSize: '0.7rem',
                                color: '#6c757d',
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                maxWidth: 'calc(100% - 40px)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {file.fileName ? file.fileName.substring(0, 16) + (file.fileName.length > 16 ? '...' : '') : 'Unknown'}
                              </div>
                              {index === 0 && (
                                <div style={{
                                  position: 'absolute',
                                  top: '8px',
                                  right: '8px',
                                  fontSize: '0.6rem',
                                  color: 'white',
                                  backgroundColor: '#1A9E96',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontWeight: 'bold'
                                }}>
                                  ACTIVE
                                </div>
                              )}
                              <button
                                onClick={() => handlePhotoDelete(file.fileId)}
                                style={{
                                  position: 'absolute',
                                  bottom: '8px',
                                  right: '8px',
                                  width: '28px',
                                  height: '28px',
                                  backgroundColor: 'white',
                                  border: '1px solid #dee2e6',
                                  borderRadius: '6px',
                                  color: '#e74c3c',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                              >
                                ✕
                              </button>
                            </>
                          )}
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </div>
              
              {/* Gallery View */}
              {!hideStyleExamples && (
                <div className="mt-4">
                  <h5 className="mb-3">Style Examples</h5>
                  <Row>
                    <Col md={4} className="mb-3">
                      <div 
                        style={{
                          width: '100%',
                          height: '200px',
                          backgroundColor: '#f8f9fa',
                          border: '2px solid #dee2e6',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '3rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => setShowComingSoonModal(true)}
                      >
                        👗
                        <div 
                          style={{
                            fontSize: '0.5rem',
                            color: 'white',
                            backgroundColor: '#6c757d',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            marginTop: '10px'
                          }}
                        >
                          COMING SOON
                        </div>
                      </div>
                      <p className="text-center mt-2 small">Casual Style</p>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div 
                        style={{
                          width: '100%',
                          height: '200px',
                          backgroundColor: '#f8f9fa',
                          border: '2px solid #dee2e6',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '3rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => setShowComingSoonModal(true)}
                      >
                        👔
                        <div 
                          style={{
                            fontSize: '0.5rem',
                            color: 'white',
                            backgroundColor: '#6c757d',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            marginTop: '10px'
                          }}
                        >
                          COMING SOON
                        </div>
                      </div>
                      <p className="text-center mt-2 small">Business Style</p>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div 
                        style={{
                          width: '100%',
                          height: '200px',
                          backgroundColor: '#f8f9fa',
                          border: '2px solid #dee2e6',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '3rem',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                        onClick={() => setShowComingSoonModal(true)}
                      >
                        🏃‍♀️
                        <div 
                          style={{
                            fontSize: '0.5rem',
                            color: 'white',
                            backgroundColor: '#6c757d',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontWeight: 'bold',
                            marginTop: '10px'
                          }}
                        >
                          COMING SOON
                        </div>
                      </div>
                      <p className="text-center mt-2 small">Athletic Style</p>
                    </Col>
                  </Row>
                </div>
              )}
            </CardBody>
          </Card>
        );

      case 3: // Body Only
        return (
          <Card className="mt-4">
            <CardBody>
              <CardTitle tag="h4" className="mb-3">Body Measurements</CardTitle>
              <Form>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="chest">Chest (cm)</Label>
                      <Input type="number" id="chest" placeholder="Chest measurement" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="waist">Waist (cm)</Label>
                      <Input type="number" id="waist" placeholder="Waist measurement" />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="hips">Hips (cm)</Label>
                      <Input type="number" id="hips" placeholder="Hips measurement" />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="inseam">Inseam (cm)</Label>
                      <Input type="number" id="inseam" placeholder="Inseam measurement" />
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="bodyType">Body Type</Label>
                  <Input type="select" id="bodyType">
                    <option>Select body type</option>
                    <option>Slim</option>
                    <option>Athletic</option>
                    <option>Average</option>
                    <option>Curvy</option>
                  </Input>
                </FormGroup>
                <Button color="primary" className="mt-3">Continue</Button>
              </Form>
            </CardBody>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Container>
      <Row className="justify-content-center">
        <Col md={10}>
          <div className="text-center mb-4">
            <h2 className="mb-3">Select Your Option</h2>
            <p className="text-muted">Choose the option that best fits your needs</p>
          </div>
          
          <Row className="justify-content-center">
            {choices.map((choice) => (
              <Col key={choice.id} xs={4} sm={6} md={4} className="mb-3">
                <Card 
                  className={`choice-card ${selectedChoice === choice.id ? 'selected' : ''} ${choice.id === 1 || choice.id === 3 ? 'disabled' : ''}`}
                  onClick={() => handleChoiceSelect(choice.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <CardBody className="text-center">
                    <div className="choice-icon mb-2">
                      <span style={{ fontSize: '1.5rem' }}>{choice.icon}</span>
                    </div>
                    <CardTitle tag="h6" className="mb-1">
                      {choice.title}
                    </CardTitle>
                    <CardText className="small text-muted mb-0 d-none d-sm-block">
                      {choice.description}
                    </CardText>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Mobile-only Dynamic Caption Text */}
          <div className="text-center mt-3 mb-4 d-block d-sm-none">
            <p className="text-muted small">
              {selectedChoice === 1 && "Upload a selfie and provide body measurements for the most accurate fit recommendations."}
              {selectedChoice === 2 && "Upload a selfie to see how different styles look on you with our face swap technology."}
              {selectedChoice === 3 && "Enter your body measurements manually for personalized clothing recommendations."}
            </p>
          </div>

                        {/* Dynamic Questions Section */}
              <div style={{ position: 'relative' }}>
                {renderQuestions()}
                
                {/* Coming Soon Overlay for options 1 and 3 */}
                {(selectedChoice === 1 || selectedChoice === 3) && (
                  <div 
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '0.375rem',
                      zIndex: 10
                    }}
                  >
                    <div className="text-white text-center">
                      <h5 className="mb-2">Coming Soon</h5>
                      <p className="small mb-0">This feature is under development</p>
                    </div>
                  </div>
                )}
                

              </div>
        </Col>
      </Row>
    </Container>
    
    {/* Coming Soon Modal */}
    <Modal isOpen={showComingSoonModal} toggle={() => setShowComingSoonModal(false)} centered>
      <ModalHeader toggle={() => setShowComingSoonModal(false)}>
        🚀 Coming Soon
      </ModalHeader>
      <ModalBody>
        <div className="text-center">
          <div className="mb-3">
            <span style={{ fontSize: '3rem' }}>⏳</span>
          </div>
          <h5 className="mb-3">These functions will be released shortly</h5>
          <p className="text-muted">
            We're working hard to bring you these exciting new features. 
            Stay tuned for updates!
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="primary" onClick={() => setShowComingSoonModal(false)}>
          Got it!
        </Button>
      </ModalFooter>
    </Modal>
    </>
  );
};

export default Upload;
